/* W12 — Evidence & Board Engine : le dossier comité/board/audit généré depuis l'historique.
 * Chaque affirmation renvoie à sa preuve. Imprimable (window.print). */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { FileText, Printer, Loader2, Radar, CheckCircle2, Gauge } from "lucide-react";

const fmtMoney = (v, cur = "CHF") => v == null ? "—" : new Intl.NumberFormat("fr-CH", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("fr-CH") : "—";

function Section({ title, children }) {
  return (
    <div className="noam-card p-4 print:border print:border-zinc-300">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#378ADD] mb-2">{title}</div>
      {children}
    </div>
  );
}

function ScoreCard() {
  const [score, setScore] = useState(null);
  useEffect(() => { api.get("/workspace/infrastructure-score").then((r) => setScore(r.data)).catch(() => {}); }, []);
  if (!score) return null;
  const m = score.measures;
  const cells = [
    ["Décisions enregistrées", m.decisions_recorded.value, m.decisions_recorded.note],
    ["Boucle fermée (outcomes)", m.decisions_closed_pct.value != null ? `${m.decisions_closed_pct.value} %` : "—", `${m.decisions_closed_pct.n} — ${m.decisions_closed_pct.note}`],
    ["Campagnes exécutées", m.recurring_obligations_pct.value != null ? `${m.recurring_obligations_pct.value} %` : "—", `${m.recurring_obligations_pct.n} — ${m.recurring_obligations_pct.note}`],
    ["Dossiers générés (90 j)", m.board_artefacts_90d.value, m.board_artefacts_90d.note],
    ["Signaux pris en charge", m.detected_before_human_pct.value != null ? `${m.detected_before_human_pct.value} %` : "—", `${m.detected_before_human_pct.n} — ${m.detected_before_human_pct.note}`],
    ["Dépendances externes (API)", m.external_dependencies.value, m.external_dependencies.note],
  ];
  return (
    <Section title="NOAM Infrastructure Score — mesures dures, jamais embellies">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-2" data-testid="infrastructure-score">
        {cells.map(([label, value, note]) => (
          <div key={label} className="border border-white/5 rounded p-2 text-center" title={note}>
            <div className="text-lg font-bold text-zinc-100">{value ?? "—"}</div>
            <div className="text-[9px] text-zinc-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SignalsPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/workspace/monitoring/signals").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const ack = async (id) => {
    try { await api.post(`/workspace/monitoring/signals/${id}/acknowledge`); notify.success("Signal pris en charge."); load(); }
    catch (e) { notify.error(fmtError(e)); }
  };
  if (!data) return null;
  return (
    <Section title={`Surveillance continue — ${data.open} signal(aux) ouvert(s)`}>
      <div className="space-y-1.5" data-testid="signals-panel">
        {(data.items || []).length === 0 && <div className="text-[10px] text-zinc-600">Aucun signal ouvert. NOAM surveille : échéances, documents modifiés, deals stagnants, décisions sans outcome, risques sans contrôle.</div>}
        {(data.items || []).map((s) => (
          <div key={s.id} className={`p-2 rounded border text-xs flex items-start gap-2 ${s.severity === "critical" ? "border-[#E24B4A]/40 bg-[#E24B4A]/5" : "border-[#EF9F27]/30 bg-[#EF9F27]/5"}`} data-testid={`signal-${s.id}`}>
            <Radar className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: s.severity === "critical" ? "#E24B4A" : "#EF9F27" }} />
            <div className="flex-1">
              <div className="text-zinc-200">{s.title}</div>
              <div className="text-[10px] text-zinc-500">{s.why} · détecté le {fmtDate(s.detected_at)}</div>
            </div>
            <button onClick={() => ack(s.id)} className="btn-ghost text-[10px] border border-white/10 print:hidden" data-testid={`signal-ack-${s.id}`}>
              <CheckCircle2 className="w-3 h-3" /> Prendre en charge
            </button>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function BoardPack() {
  const [pack, setPack] = useState(null);
  const [period, setPeriod] = useState(90);
  useEffect(() => {
    api.get(`/workspace/board-pack?period_days=${period}`).then((r) => setPack(r.data))
      .catch((e) => notify.error(fmtError(e)));
  }, [period]);

  if (!pack) return <div className="flex justify-center py-16 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const g = pack.governance, ex = pack.execution;
  return (
    <div className="space-y-4 max-w-5xl print:text-black" data-testid="board-pack-page">
      <div className="flex items-center gap-3 flex-wrap">
        <FileText className="w-5 h-5 text-[#378ADD]" />
        <div className="flex-1">
          <h1 className="noam-display text-xl font-bold text-zinc-100">Dossier Comité — {pack.organisation}</h1>
          <div className="text-[10px] text-zinc-500">Généré le {fmtDate(pack.generated_at)} par {pack.generated_by} · période {pack.period_days} jours · {pack.note}</div>
        </div>
        <select value={period} onChange={(e) => setPeriod(Number(e.target.value))} className="noam-input text-[10px] w-32 print:hidden" data-testid="board-pack-period">
          <option value={30}>30 jours</option><option value={90}>90 jours</option><option value={180}>180 jours</option><option value={365}>365 jours</option>
        </select>
        <button onClick={() => window.print()} className="btn-primary text-[10px] print:hidden" data-testid="board-pack-print">
          <Printer className="w-3 h-3" /> Imprimer / PDF
        </button>
      </div>

      <ScoreCard />
      <SignalsPanel />

      <Section title={`Ce que nous avons décidé — ${pack.decisions.length} décision(s)`}>
        <div className="space-y-2" data-testid="board-pack-decisions">
          {pack.decisions.length === 0 && <div className="text-[10px] text-zinc-600">Aucune décision sur la période.</div>}
          {pack.decisions.map((d) => (
            <div key={d.id} className="border border-white/5 rounded p-2.5 text-xs space-y-0.5">
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-zinc-100 font-medium">{d.title}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">{d.verdict || d.status}</span>
                <span className="text-[10px] text-zinc-500">{d.decided_by} · {fmtDate(d.decided_at)}</span>
              </div>
              {d.what_we_knew && <div className="text-[10px] text-zinc-500">Ce que nous savions : P(succès) {d.what_we_knew.p_success != null ? Math.round(d.what_we_knew.p_success * 100) + " %" : "—"} · AdjEV {fmtMoney(d.what_we_knew.adjusted_ev_chf, d.currency)}</div>}
              {d.memory_said && <div className="text-[10px] text-[#7F77DD]">Ce que la mémoire disait : {d.memory_said}</div>}
              {d.rationale && <div className="text-[10px] text-zinc-400">Pourquoi : {d.rationale}</div>}
              {d.outcome && <div className="text-[10px]">Ce qui s'est produit : <b className={d.outcome.payoff_realized_chf > 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{fmtMoney(d.outcome.payoff_realized_chf, d.currency)}</b> le {fmtDate(d.outcome.recorded_at)}</div>}
              {!d.outcome && <div className="text-[10px] text-[#EF9F27]">Outcome non enregistré — boucle ouverte.</div>}
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Registre Governance — ${g.validated.length} validé(s), ${g.rejected.length} écarté(s), ${g.documents.length} source(s) sous surveillance`}>
        <div className="space-y-1 text-[11px]" data-testid="board-pack-governance">
          {g.validated.length + g.rejected.length + g.documents.length === 0 && (
            <div className="text-[10px] text-zinc-600">Aucune activité de registre sur la période.</div>
          )}
          {g.validated.map((v, i) => (
            <div key={i} className="text-zinc-300">✔ {v.materialized_code ? `${v.materialized_code} — ` : ""}{v.title}
              <span className="text-zinc-500"> · source « {v.source} » · validé par {v.by} le {fmtDate(v.at)}</span>
              {v.excerpt && <div className="text-[10px] text-zinc-600 italic ml-4">« {String(v.excerpt).slice(0, 160)}… »</div>}
            </div>
          ))}
          {g.rejected.map((v, i) => (
            <div key={`r${i}`} className="text-zinc-400">✖ {v.title} <span className="text-zinc-500">· écarté par {v.by} : {v.reason}</span></div>
          ))}
          {g.documents.map((d, i) => (
            <div key={`d${i}`} className="text-[10px] text-zinc-500">Source « {d.label} » v{d.version} · dernier changement {fmtDate(d.last_change)}</div>
          ))}
        </div>
      </Section>

      <Section title={`Exécution — ${ex.obligations_resolved.length} engagement(s) tenu(s) avec preuve, ${ex.still_open.length} ouvert(s)`}>
        <div className="space-y-1 text-[11px]" data-testid="board-pack-execution">
          {ex.obligations_resolved.map((o, i) => (
            <div key={i} className="text-zinc-300">✔ {o.title} <span className="text-zinc-500">· {o.owner || "—"} · preuve : {o.proof}{o.evidence ? ` (${o.evidence})` : ""} · {fmtDate(o.at)}</span></div>
          ))}
          {ex.still_open.map((o, i) => (
            <div key={`o${i}`} className="text-[#EF9F27]">⧗ {o.title} <span className="text-zinc-500">· {o.owner || "sans responsable"} · échéance {fmtDate(o.due_at)}{o.consequence ? ` · si non fait : ${o.consequence}` : ""}</span></div>
          ))}
        </div>
      </Section>

      <Section title={`Récurrence & incidents — ${pack.recurrence.campaigns.length} campagne(s), ${pack.recurrence.incidents.length} incident(s) sur la période`}>
        <div className="space-y-1 text-[11px]">
          {pack.recurrence.campaigns.map((c, i) => (
            <div key={i} className="text-zinc-300">{c.active ? "●" : "○"} {c.title} <span className="text-zinc-500">· {c.frequency} · {c.owner} · prochaine occurrence {fmtDate(c.next_run_at)}</span></div>
          ))}
          {pack.recurrence.incidents.map((inc, i) => (
            <div key={`i${i}`} className={inc.status === "open" ? "text-[#E24B4A]" : "text-zinc-400"}>
              {inc.status === "open" ? "⚠" : "✔"} {inc.title} <span className="text-zinc-500">· sévérité {inc.severity}/5 · {fmtDate(inc.occurred_at)}{inc.resolution ? ` · résolu : ${inc.resolution}` : ""}</span>
            </div>
          ))}
        </div>
      </Section>

      {pack.meetings.length > 0 && (
        <Section title={`Séances & comités — ${pack.meetings.length}`}>
          {pack.meetings.map((m) => (
            <div key={m.id} className="text-[11px] text-zinc-300">{m.title} · {fmtDate(m.held_at)} · {m.attendees.join(", ")} · {m.decision_ids.length} décision(s) rattachée(s)</div>
          ))}
        </Section>
      )}
    </div>
  );
}
